import * as React from 'react';
import { Rozet } from '@/components/ui/card';
import { api, kuyrukIzleyiciBaslat, oturum } from '@/lib/api';
import { useYeniSurum, yenidenYukle } from '@/lib/surum';
import { AdminApp } from '@/features/admin/AdminApp';
import { GirisEkrani } from '@/features/auth/GirisEkrani';
import { OnlineRezervasyon } from '@/features/booking/OnlineRezervasyon';
import { MisafirMobil } from '@/features/guest/MisafirMobil';
import { MisafirMobilAyar } from '@/features/settings/MisafirMobilAyar';
import { SiteyeEkle } from '@/features/booking/SiteyeEkle';
import { UyumTakvimi } from './features/compliance/UyumTakvimi';
import { Kullanicilar } from '@/features/users/Kullanicilar';
import { Kanallar } from '@/features/channels/Kanallar';
import { CheckInScreen, type CheckInRezervasyon } from '@/features/checkin/CheckInScreen';
import { KbsPaneli } from '@/features/kbs/KbsPaneli';
import { KatHizmetleri } from '@/features/housekeeping/KatHizmetleri';
import { Raporlar } from '@/features/reports/Raporlar';
import { OdaPlani } from '@/features/roomplan/OdaPlani';
import { TelefonTarayici } from '@/features/scanner/TelefonTarayici';
import { FiyatTakvimi } from '@/features/rates/FiyatTakvimi';
import { Odalar } from '@/features/rooms/Odalar';
import { Faturalar } from '@/features/invoices/Faturalar';
import { GelenKutusu } from '@/features/inbox/GelenKutusu';
import { ceviri } from '@/locales';

export type Sekme = 'plan' | 'gelen' | 'odalar' | 'fiyat' | 'fatura' | 'kbs' | 'kat' | 'kanallar' | 'raporlar' | 'site' | 'kullanicilar' | 'misafir' | 'uyum';

/**
 * Her sekmenin gerektirdigi yetki.
 *
 * Sekmeler bu listeden uretilir; kullanamayacagi bir sekmeyi kimse gormez.
 * Onceden dort temel sekme herkese aciktu ve kat gorevlisi KBS ya da
 * Raporlar'a basinca 403 aliyordu.
 */
/**
 * Sekmelerin ekrandaki sirasi VE tek kaynagi.
 *
 * Onceden bu liste iki yerde ayri ayri yaziliydi (izin suzgeci ve sekme
 * cubugu). Yeni bir sekme birine eklenip digerine eklenmeyince sekme
 * gorunuyor ama BASILINCA ACILMIYORDU - hicbir hata da vermiyordu.
 * Artik ikisi de buradan uretiliyor; testi: test/sekmeler.test.ts
 */
export const SEKME_SIRASI = [
  'plan',
  'gelen',
  'odalar',
  'fiyat',
  'fatura',
  'kbs',
  'kat',
  'kanallar',
  'raporlar',
  'uyum',
  'kullanicilar',
  'misafir',
  'site',
] as const satisfies readonly Sekme[];

/** Yaninda kirmizi nokta yanip sonen sekmeler (ilgi bekleyen is var). */
const UYARI_SEKMELERI = new Set<Sekme>(['kbs']);

export const SEKME_YETKISI: Record<Sekme, string> = {
  plan: 'roomplan:read',
  gelen: 'reservation:write',
  odalar: 'room:write',
  fiyat: 'reservation:read',
  fatura: 'folio:read',
  kbs: 'kbs:read',
  kat: 'housekeeping:read',
  kanallar: 'agency:read',
  raporlar: 'report:read',
  kullanicilar: 'user:manage',
  site: 'tenant:settings',
  misafir: 'tenant:settings',
  uyum: 'uyum:read',
};

interface Kimlik {
  kullanici: { id: string; ad: string; tenantId: string; roller: string[] };
  yetkiler: string[];
}

export default function App() {
  // otelcms.com/admin -> platform yonetim paneli.
  // Tesis adreslerinde bu yol sunucuda 404 dondugu icin panel acilmaz.
  if (location.pathname === '/admin' || location.pathname.startsWith('/admin/')) {
    return <AdminApp />;
  }
  // Misafire acik rezervasyon sayfasi: oturum istemez, tesis HOST'tan cozulur.
  // /rezervasyon/embed, otelin kendi sitesine gomulen iframe icin sadelestirilmis
  // surumdur (ust bant ve alt bilgi yok, arka plan seffaf).
  if (location.pathname === '/rezervasyon/embed') return <OnlineRezervasyon gomulu />;
  if (location.pathname === '/rezervasyon' || location.pathname.startsWith('/rezervasyon/')) {
    return <OnlineRezervasyon />;
  }
  // Odadaki QR: /oda/104. Oturum istemez; oda numarasi adresten okunur ve
  // link check-out'ta sunucuda kapanir (bkz. features/guest/MisafirMobil).
  const oda = /^\/oda\/([^/]+)\/?$/.exec(location.pathname);
  if (oda) return <MisafirMobil odaNo={decodeURIComponent(oda[1]!)} />;
  // Gorevlinin telefonu: masadaki QR'i okutunca acilir. Kod ADRES PARCASINDA
  // (#) tasinir - sorgu dizesi olsaydi sunucu gunlugune ve Referer basligina
  // duserdi. Kendi personel oturumunu ister; QR tek basina yetki vermez.
  if (location.pathname === '/tarayici' || location.pathname.startsWith('/tarayici/')) {
    return <TelefonTarayici />;
  }
  return <OtelPaneli />;
}

function OtelPaneli() {
  const t = ceviri();
  const [kimlik, setKimlik] = React.useState<Kimlik | null>(null);
  const [sekme, setSekme] = React.useState<Sekme | null>(null);
  const [checkInRez, setCheckInRez] = React.useState<CheckInRezervasyon | null>(null);
  const [bosOdalar, setBosOdalar] = React.useState<{ id: string; no: string; temizlikDurumu: string }[]>([]);
  const [cevrimici, setCevrimici] = React.useState(navigator.onLine);
  const [bekleyen, setBekleyen] = React.useState(0);
  const [acikTalep, setAcikTalep] = React.useState(0);
  const sekmeCubugu = React.useRef<HTMLElement>(null);
  /*
   * Yeni surum uyarisi.
   *
   * Resepsiyon sekmesi gunlerce acik kalir; uygulama acildiktan sonra
   * index.html'i bir daha istemedigi icin dagitimdan sonra ekran ESKI
   * paketle calismaya devam eder - yeni ozellik "gelmemis" gorunur.
   * Kendiliginden yenilemiyoruz: gorevli yarim kalmis bir check-in'in
   * ortasinda olabilir.
   */
  const yeniSurum = useYeniSurum();
  const [surumGizli, setSurumGizli] = React.useState(false);

  React.useEffect(() => {
    if (!oturum.token()) return;
    api<Kimlik>('/api/auth/ben')
      .then(setKimlik)
      .catch(() => oturum.temizle());
  }, []);

  /*
   * Misafir talebi sayaci ust bantta durur.
   *
   * Tasarim "Resepsiyon ekranında · aynı anda" diyor; talep yalnizca kat
   * hizmetleri sekmesinde gorunurse, resepsiyonist oda planindayken haberi
   * olmaz. Rozet her sekmede gorunur ve tiklayinca listeye goturur.
   * Canli yayin zaten kat hizmetleri ekranini besliyor; buradaki sayac
   * daha seyrek (60 sn) yoklanir - amaci uyarmak, is yaptirmak degil.
   */
  React.useEffect(() => {
    if (!kimlik?.yetkiler.includes('housekeeping:read')) return;
    const oku = () =>
      void api<{ talepler: unknown[] }>('/api/misafir-talepleri')
        .then((v) => setAcikTalep(v.talepler.length))
        .catch(() => {});
    oku();
    const z = setInterval(() => document.visibilityState === 'visible' && oku(), 60_000);
    return () => clearInterval(z);
  }, [kimlik]);

  /*
   * Acik sekme kaydirilabilir cubugun disinda kalmasin. Ozellikle son
   * sekmelerde (Misafir mobil, Sitenize ekleyin) kullanici sekmeyi secip
   * geri dondugunde cubuk basa sarilmis oluyordu.
   */
  React.useEffect(() => {
    const aktif = sekmeCubugu.current?.querySelector('[data-aktif]');
    aktif?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [sekme, kimlik]);

  React.useEffect(() => {
    const cevrimiciDinle = () => setCevrimici(navigator.onLine);
    window.addEventListener('online', cevrimiciDinle);
    window.addEventListener('offline', cevrimiciDinle);
    const durdur = kuyrukIzleyiciBaslat((s) => setBekleyen(s.kalan));
    return () => {
      window.removeEventListener('online', cevrimiciDinle);
      window.removeEventListener('offline', cevrimiciDinle);
      durdur();
    };
  }, []);

  const SurumBandi = () =>
    yeniSurum && !surumGizli ? (
      <div className="flex flex-wrap items-center gap-3 bg-marka-600 px-4 py-2 text-white">
        <span className="text-[13.5px] font-medium">
          Yeni sürüm yayınlandı. Yenilemeden eski ekranı kullanmaya devam edersiniz.
        </span>
        <button
          onClick={() => void yenidenYukle()}
          className="rounded-full bg-white px-3 py-1 text-[13px] font-semibold text-marka-600"
        >
          Yenile
        </button>
        <button
          onClick={() => setSurumGizli(true)}
          aria-label="Uyarıyı gizle"
          className="ml-auto text-[13px] text-white/70 underline underline-offset-4"
        >
          Sonra
        </button>
      </div>
    ) : null;

  if (!kimlik) return <GirisEkrani onGiris={setKimlik} />;

  if (checkInRez) {
    return (
      <>
        <SurumBandi />
      <CheckInScreen
        rezervasyon={checkInRez}
        bosOdalar={bosOdalar}
        tenantId={kimlik.kullanici.tenantId}
        onKapat={() => setCheckInRez(null)}
        onTamamlandi={() => setCheckInRez(null)}
      />
      </>
    );
  }

  /** Tasarimdaki hap seklinde sekme (otelcms Raporlar.dc.html). */
  // Acilis sekmesi: kat gorevlisi (rezervasyon okuyamaz ama kat hizmetlerini
  // yonetir) dogruca kendi ekraniyla acilir; digerleri oda planiyla.
  // Kanca (hook) DEGIL: bu satirin ustunde erken return'ler var, useMemo
  // kullanilirsa render'lar arasi kanca sayisi degisir (React #310).
  const izinli = SEKME_SIRASI.filter((k) => kimlik.yetkiler.includes(SEKME_YETKISI[k]));
  const varsayilan: Sekme =
    kimlik.yetkiler.includes('housekeeping:read') && !kimlik.yetkiler.includes('reservation:read')
      ? 'kat'
      : (izinli[0] ?? 'plan');
  const aktifSekme: Sekme = sekme && izinli.includes(sekme) ? sekme : varsayilan;

  /**
   * Sekme cubugu.
   *
   * On sekme var; 390px'de hepsi sigmiyordu ve tasan kisim KIRPILIYORDU -
   * telefonda Kat hizmetleri, Raporlar, e-Fatura'ya ulasmak mumkun degildi.
   * Cozum yatay kaydirma: cubuk kendi icinde kayar, cubugun kendisi tam
   * genislik alir ve acik olan sekme kendiliginden goruse getirilir.
   * Masaustunde hicbir sey degismez (hepsi zaten sigiyor).
   */
  /** Sekme etiketleri. Anahtarlar Sekme tipinden gelir; eksik birakilamaz. */
  const SEKME_ETIKET: Record<Sekme, string> = {
    plan: t('odaPlani'),
    gelen: 'Gelen kutusu',
    odalar: 'Odalar',
    fiyat: 'Fiyat takvimi',
    fatura: 'e-Fatura',
    kbs: t('kbsDurum'),
    kat: t('housekeeping'),
    kanallar: 'Kanallar',
    raporlar: t('raporlar'),
    uyum: 'Uyum takvimi',
    kullanicilar: 'Kullanıcılar',
    misafir: 'Misafir mobil',
    site: 'Sitenize ekleyin',
  };

  const Sekmeler = () => (
    <nav
      ref={sekmeCubugu}
      className="flex w-full gap-1 overflow-x-auto rounded-full bg-zemin p-1 [-ms-overflow-style:none] [scrollbar-width:none] lg:w-auto [&::-webkit-scrollbar]:hidden"
    >
      {/* Sira ve izin suzgeci `izinli` ile ORTAK - ikisi ayrilamaz. */}
      {izinli.map((k) => {
        const etiket = SEKME_ETIKET[k];
        const uyari = UYARI_SEKMELERI.has(k);
        return (
        <button
          key={k}
          data-aktif={aktifSekme === k || undefined}
          onClick={() => setSekme(k)}
          className={
            'flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors sm:px-[18px] ' +
            (aktifSekme === k ? 'bg-lacivert text-white' : 'text-gri hover:text-lacivert')
          }
        >
          {etiket}
          {uyari && aktifSekme !== k && (
            <span className="size-1.5 rounded-full bg-kirmizi [animation:blink_1.4s_infinite]" />
          )}
        </button>
        );
      })}
    </nav>
  );

  return (
    <div className="flex min-h-dvh flex-col bg-zemin">
      <SurumBandi />
      <header className="sticky top-0 z-20 border-b border-cizgi bg-white">
        <div className="flex min-h-16 flex-wrap items-center gap-x-7 gap-y-3 px-4 py-3 sm:px-7">
          <span className="order-1 flex items-center gap-2.5 text-[19px] font-bold tracking-[-.02em] text-lacivert">
            <svg width="28" height="28" viewBox="0 0 30 30" fill="none" stroke="#1f4fd8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="3" y="7" width="24" height="20" rx="4" />
              <path d="M9 7V4m12 3V4M3 13h24" />
              <path d="M10 19h3m4 0h3" stroke="#f2b632" />
            </svg>
            otelcms
          </span>
          {/* Mobilde sekmeler ALT satira, tam genislige gecer; lg'de sirasi eski haline doner. */}
          <div className="order-3 w-full min-w-0 lg:order-2 lg:w-auto">
            <Sekmeler />
          </div>
          <div className="order-2 ml-auto flex flex-wrap items-center justify-end gap-x-4 gap-y-2 text-sm text-gri lg:order-3">
            {!cevrimici && <Rozet renk="uyari">{t('cevrimdisi')}</Rozet>}
            {bekleyen > 0 && (
              <Rozet renk="bilgi">
                {bekleyen} {t('bekleyenIslem')}
              </Rozet>
            )}
            {acikTalep > 0 && aktifSekme !== 'kat' && (
              <button
                onClick={() => setSekme('kat')}
                title="Misafir talepleri"
                className="flex items-center gap-2 rounded-full bg-sari/20 px-3 py-1.5 text-[13px] font-semibold text-lacivert transition-colors hover:bg-sari/35"
              >
                <span className="size-2 rounded-full bg-sari [animation:blink_1.4s_infinite]" />
                {acikTalep} misafir talebi
              </button>
            )}
            <span className="flex items-center gap-2 whitespace-nowrap">
              <span className="size-2 rounded-full bg-yesil" />
              {/* Dar ekranda ad gizlenir; yesil nokta "oturum acik" bilgisini zaten veriyor. */}
              <span className="hidden sm:inline">{kimlik.kullanici.ad}</span>
            </span>
            <button
              onClick={() => {
                void api('/api/auth/cikis', { method: 'POST' }).catch(() => {});
                oturum.temizle();
                setKimlik(null);
              }}
              className="rounded-full border-[1.5px] border-cizgi px-3.5 py-2 font-semibold text-lacivert transition-colors hover:border-lacivert"
            >
              {t('cikisYap')}
            </button>
          </div>
        </div>
      </header>

      {izinli.length === 0 ? (
        <main className="flex flex-1 items-center justify-center bg-zemin p-7">
          <div className="max-w-[40ch] rounded-[20px] border border-cizgi bg-white p-7 text-center">
            <h2 className="text-lg font-bold text-lacivert">Bu hesap için açık ekran yok</h2>
            <p className="mt-2 text-sm leading-relaxed text-gri">
              Hesabınıza henüz bir görev tanımlanmamış görünüyor. Tesis yöneticiniz Kullanıcılar
              ekranından rolünüzü belirleyince ilgili ekranlar açılır.
            </p>
          </div>
        </main>
      ) : aktifSekme === 'plan' ? (
        <OdaPlani
          tenantId={kimlik.kullanici.tenantId}
          yetkiler={kimlik.yetkiler}
          onCheckIn={async (rezId) => {
            // Oda plani ekraninda cift tiklanan rezervasyonun detayini yukle
            const veri = await api<{ rezervasyon: CheckInRezervasyon; bosOdalar: typeof bosOdalar }>(
              `/api/rezervasyonlar/${rezId}/checkin-hazirlik`,
            ).catch(() => null);
            if (veri) {
              setBosOdalar(veri.bosOdalar);
              setCheckInRez(veri.rezervasyon);
            }
          }}
        />
      ) : aktifSekme === 'gelen' ? (
        <GelenKutusu />
      ) : aktifSekme === 'odalar' ? (
        <Odalar yetkiler={kimlik.yetkiler} />
      ) : aktifSekme === 'fiyat' ? (
        <FiyatTakvimi yetkiler={kimlik.yetkiler} />
      ) : aktifSekme === 'fatura' ? (
        <Faturalar yetkiler={kimlik.yetkiler} />
      ) : aktifSekme === 'kbs' ? (
        <KbsPaneli yetkiler={kimlik.yetkiler} />
      ) : aktifSekme === 'kat' ? (
        <KatHizmetleri tenantId={kimlik.kullanici.tenantId} yetkiler={kimlik.yetkiler} />
      ) : aktifSekme === 'kanallar' ? (
        <Kanallar yetkiler={kimlik.yetkiler} />
      ) : aktifSekme === 'uyum' ? (
        <UyumTakvimi yazabilir={kimlik.yetkiler.includes('uyum:write')} />
      ) : aktifSekme === 'kullanicilar' ? (
        <Kullanicilar />
      ) : aktifSekme === 'misafir' ? (
        <MisafirMobilAyar />
      ) : aktifSekme === 'site' ? (
        <SiteyeEkle />
      ) : (
        <Raporlar yetkiler={kimlik.yetkiler} />
      )}
    </div>
  );
}
